// ============================================================
// ENGINE V2 — ORCHESTRATOR
// ============================================================
// Single public entry point for the career engine.
//
// Two flows:
//
//   runCareerEngine()
//     Called after a student completes an activity.
//     Runs: Mutation Engine → Suggestion Engine
//     Returns updated traits + ranked career suggestions.
//
//   selectCareerPath()
//     Called when a student manually selects a new career
//     path node from the UI.
//     Runs: Decision Engine (guard validation only)
//     Returns updated active nodes if allowed.
//
//   traverseCareerPath()
//     Called when a student advances from a parent node
//     to a child node (e.g. STEM → STEM.Engineering).
//     Runs: Decision Engine with traversal logic.
//     Marks parent as completed, adds child as active.
// ============================================================

import type {
  CareerEngineInput,
  CareerEngineOutput,
  StudentProfile,
  CareerPathNode,
  DecisionResult,
} from './types';
import { runMutationEngine }  from './mutation-engine';
import { runSuggestionEngine } from './suggestion-engine';
import {
  runDecisionEngine,
  traverseCareerPath as decisionTraverse,
} from './decision-engine';

// ─── MAIN ENGINE RUN ─────────────────────────────────────────
// Triggered by an activity completion event.

export function runCareerEngine(input: CareerEngineInput): CareerEngineOutput {
  const { student, activityResult, careerTree } = input;

  // Phase 1: Mutation Engine
  // Extract trait results from the activity, apply the diffing
  // algorithm to affected career path nodes, recompute general traits.
  const mutation = runMutationEngine(student, activityResult);

  // Phase 2: Build the updated student profile for the suggestion engine
  const updatedStudent: StudentProfile = {
    ...student,
    activeNodes:   mutation.updatedActiveNodes,
    generalTraits: mutation.updatedGeneralTraits,
  };

  // Phase 3: Suggestion Engine
  // Using the freshly mutated trait scores, resolve which career nodes
  // are in scope, compute distances against minimum thresholds, and
  // return ranked suggestions.
  const suggestions = runSuggestionEngine(
    updatedStudent,
    careerTree,
    mutation.updatedGeneralTraits
  );

  return { mutation, suggestions };
}

// ─── CAREER PATH SELECTION ───────────────────────────────────
// Called when a student picks a new career path node.
// Does not trigger mutation or suggestions — only validates
// and updates the student's active nodes list.

export function selectCareerPath(
  student: StudentProfile,
  targetNode: CareerPathNode
): DecisionResult {
  return runDecisionEngine(student, targetNode);
}

// ─── CAREER PATH TRAVERSAL ───────────────────────────────────
// Called when a student advances from a parent to a child node.
// Parent is marked 'completed', child is added as 'active'.

export function traverseCareerPath(
  student: StudentProfile,
  targetNode: CareerPathNode
): DecisionResult {
  return decisionTraverse(student, targetNode);
}

// ─── RE-EXPORTS ──────────────────────────────────────────────
// Everything a consumer needs without digging into sub-modules.

export type {
  // Profiles
  StudentProfile,
  ActiveNode,
  GeneralTrait,
  CareerPathTrait,

  // Career tree
  CareerPathNode,

  // Activities
  ActivityResult,
  ActivityTrait,

  // Engine outputs
  CareerEngineInput,
  CareerEngineOutput,
  DecisionResult,
  MutationResult,
  SuggestionResult,
  SuggestionObject,
  TraitDistance,

  // Primitives
  NodeLevel,
  BroadCategory,
  ActivityType,
  NodeStatus,
  SuggestionScope,
} from './types';
